import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { HYDRATE } from "next-redux-wrapper";

import { getApiBaseUrl } from "@/config/config";

export const microservicesApi = createApi({
    reducerPath: "microservicesApi",
    baseQuery: fetchBaseQuery({
        baseUrl: getApiBaseUrl(),
        prepareHeaders: async (headers) => {
            headers.set("Content-Type", "application/json");
            return headers;
        },
    }),
    extractRehydrationInfo(action, { reducerPath }) {
        if (action.type === HYDRATE) {
            return action.payload[reducerPath];
        }
    },
    tagTypes: [
        "property",
        "search",
        "development",
        "digital_card",
        "newsletter",
        "requirement",
        "valuation",
        "form",
    ],
    endpoints: (build) => ({
        getProperties: build.query({
            query: ({ body }) => {
                return {
                    url: "/search",
                    method: "POST", // or 'PUT'
                    body: JSON.stringify(body), // data can be `string` or {object}!
                    headers: {
                        "Content-Type": "application/json",
                    },
                };
            },
            providesTags: (result, error, arg) => (result ? [{ type: "search" }] : []),
        }),
        getPropertyById: build.query({
            query: (property_id) => {
                return {
                    url: `/property/${property_id}`,
                    method: "GET",
                };
            },
        }),
        getDevelopments: build.query({
            query: ({ body }) => {
                return {
                    url: "/searchDevs",
                    method: "POST", // or 'PUT'
                    body: JSON.stringify(body), // data can be `string` or {object}!
                    headers: {
                        "Content-Type": "application/json",
                    },
                };
            },
            providesTags: (result, error, arg) => (result ? [{ type: "search" }] : []),
        }),
        getDevelopmentById: build.query({
            query: (development_id) => {
                return {
                    url: `/development/${development_id}`,
                    method: "GET",
                };
            },
            providesTags: (result, error, arg) =>
                result ? [{ type: "development", id: arg }] : [],
        }),
        getDevelopmentByIdM: build.mutation({
            query: (development_id) => {
                return {
                    url: `/development/${development_id}`,
                    method: "GET",
                };
            },
            providesTags: (result, error, arg) =>
                result ? [{ type: "development", id: arg }] : [],
        }),
        getDevelopmentModel: build.query({
            query: (model_id) => {
                return {
                    url: `/developmentModel/${model_id}`,
                    method: "GET",
                };
            },
            providesTags: (result, error, arg) =>
                result ? [{ type: "development", id: arg }] : [],
        }),
        getDevelopmentSelection2d: build.query({
            query: (id) => {
                return {
                    url: `/development/2D/${id}`,
                    method: "GET",
                };
            },
            providesTags: (result, error, arg) =>
                result ? [{ type: "development", id: arg }] : [],
        }),
        getDigitalCardById: build.query({
            query: (card_id) => {
                return {
                    url: `/digital/card/${card_id}`,
                    method: "GET",
                };
            },
            providesTags: (result, error, arg) =>
                result ? [{ type: "digital_card", id: arg }] : [],
        }),
        getInventoryDevelopment: build.query({
            query: (code) => {
                const url = "development/inventory/melba";
                const param = { development_wiggot_code: code };
                return { url, method: "POST", body: param };
            },
        }),
        postLeadResetPassword: build.mutation({
            query: (lead_id) => {
                return {
                    url: `/leadResetPassword/${lead_id}`,
                    method: "POST",
                };
            },
            providesTags: (result, error, arg) =>
                result ? [{ type: "digital_card", id: arg }] : [],
        }),
        postNewsletterSubscription: build.mutation({
            query: (body) => {
                return {
                    url: "/newsletter/subscription",
                    method: "POST",
                    body,
                };
            },
            providesTags: (result, error, arg) => (result ? [{ type: "newsletter", id: arg }] : []),
        }),
        postRequirement: build.mutation({
            query: (body) => {
                return {
                    url: "/requirement",
                    method: "POST",
                    body,
                };
            },
            providesTags: (result, error, arg) =>
                result ? [{ type: "requirement", id: arg }] : [],
        }),
        postCotización: build.mutation({
            query: (body) => {
                return {
                    url: "/cotizar",
                    method: "POST",
                    body,
                };
            },
            providesTags: (result, error, arg) => (result ? [{ type: "cotizar", id: arg }] : []),
        }),
        postValuation: build.mutation({
            query: (body) => {
                return {
                    url: "/valuation",
                    method: "POST",
                    body,
                };
            },
            providesTags: (result, error, arg) => (result ? [{ type: "valuation", id: arg }] : []),
        }),
        postNewDevelopmentProcess: build.mutation({
            query: (body) => {
                return {
                    url: "/newDevelopmentProcess",
                    method: "POST",
                    body,
                };
            },
            providesTags: (result, error, arg) =>
                result ? [{ type: "newDevelopmentProcess", id: arg }] : [],
        }),
        postForm: build.mutation({
            query: (body) => {
                return {
                    url: "/hbs-form",
                    method: "POST",
                    body,
                };
            },
            providesTags: (result, error, arg) => (result ? [{ type: "form", id: arg }] : []),
        }),
        getPDFQuotation: build.mutation({
            query: ({ property_id, development__id }) => {
                return {
                    url: `/tasks/quotation/${property_id}/${development__id}`,
                    method: "GET",
                    responseHandler: async (response) =>
                        window.open(window.URL.createObjectURL(await response.blob()), "_blank"),
                    cache: "no-cache",
                };
            },
            providesTags: (result, error, arg) => (result ? [{ type: "quotation", id: arg }] : []),
        }),
        getNewDevelopment: build.mutation({
            query: ({ development_id, property_id }) => {
                const url = `/plan/calculate/development/${development_id}/property/${property_id}`;
                return { url, method: "GET" };
            },
            providesTags: (result) => (result ? [{ type: "development", id: result }] : []),
        }),
        postLogger: build.mutation({
            query: (body) => {
                const url = "/quoter/log";
                return {
                    url,
                    method: "POST",
                    body,
                };
            },
        }),
        postFormDb: build.mutation({
            query: (body) => {
                return {
                    url: "/sendFormDb",
                    method: "POST",
                    body,
                };
            },
        }),
        getDevelopmentBranding: build.mutation({
            query: (id) => {
                return {
                    url: `/branding/${id}`,
                    method: "GET",
                };
            },
        }),
        getDevelopmentVisionById: build.mutation({
            query: (id) => {
                return {
                    url: `/vision/development/${id}`,
                    method: "GET",
                };
            },
        }),
        postLoanSimulation: build.mutation({
            query: (body) => {
                return {
                    url: "/guialink/loansimulator",
                    method: "POST",
                    body,
                };
            },
        }),
        postDevFavorites: build.mutation({
            query: ({ body, token }) => ({
                url: "/user/favorites",
                method: "POST",
                body,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            }),
        }),
    }),
});

export const {
    useGetPropertiesQuery,
    useGetPropertyByIdQuery,
    useGetDevelopmentsQuery,
    useGetDevelopmentByIdQuery,
    useGetDevelopmentByIdMMutation,
    useGetDevelopmentModelQuery,
    useGetDevelopmentSelection2dQuery,
    useGetDigitalCardByIdQuery,
    useGetInventoryDevelopmentQuery,
    usePostLeadResetPasswordMutation,
    usePostNewsletterSubscriptionMutation,
    usePostRequirementMutation,
    usePostCotizaciónMutation,
    usePostValuationMutation,
    usePostNewDevelopmentProcessMutation,
    usePostFormMutation,
    useGetPDFQuotationMutation,
    useGetNewDevelopmentMutation,
    usePostLoggerMutation,
    usePostFormDbMutation,
    useGetDevelopmentBrandingMutation,
    useGetDevelopmentVisionByIdMutation,
    usePostLoanSimulationMutation,
    usePostDevFavoritesMutation,
    util: { getRunningQueriesThunk },
} = microservicesApi;

export const { getDevelopmentById } = microservicesApi.endpoints;
